import { useState, useEffect } from 'react'
import { API_BASE } from '../lib/supabase'
import { usePredictions } from '../hooks/usePredictions'
import { useAuth } from '../hooks/useAuth'
import AuthModal from '../components/AuthModal'

const BUCKETS = [
  { key: 'down15+', label: '< −15%', dir: 'down' },
  { key: 'down10to15', label: '−10 to −15%', dir: 'down' },
  { key: 'down5to10', label: '−5 to −10%', dir: 'down' },
  { key: 'down2to5', label: '−2 to −5%', dir: 'down' },
  { key: 'flat', label: '−2 to +2%', dir: 'up' },
  { key: 'up2to5', label: '+2 to +5%', dir: 'up' },
  { key: 'up5to10', label: '+5 to +10%', dir: 'up' },
  { key: 'up10to15', label: '+10 to +15%', dir: 'up' },
  { key: 'up15+', label: '> +15%', dir: 'up' },
]

function isFutureDate(dateStr) {
  const et = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }))
  const todayET = `${et.getFullYear()}-${String(et.getMonth()+1).padStart(2,'0')}-${String(et.getDate()).padStart(2,'0')}`
  return dateStr > todayET
}

function parseMktCapRaw(str) {
  if (!str) return 0
  const s = str.replace('$', '').replace(/,/g, '').trim()
  if (s.endsWith('T')) return parseFloat(s) * 1_000_000_000
  if (s.endsWith('B')) return parseFloat(s) * 1_000_000
  if (s.endsWith('M')) return parseFloat(s) * 1_000
  return parseFloat(s) || 0
}

const SORT_OPTIONS = [
  { key: 'default',  label: 'Default' },
  { key: 'ticker',   label: 'Ticker A–Z' },
  { key: 'name',     label: 'Name A–Z' },
  { key: 'cap_desc', label: 'Mkt Cap ↓' },
  { key: 'cap_asc',  label: 'Mkt Cap ↑' },
]

function sortStocks(stocks, sortKey) {
  const s = [...stocks]
  switch (sortKey) {
    case 'ticker':   return s.sort((a, b) => a.ticker.localeCompare(b.ticker))
    case 'name':     return s.sort((a, b) => (a.name || a.ticker).localeCompare(b.name || b.ticker))
    case 'cap_desc': return s.sort((a, b) => parseMktCapRaw(b.mkt_cap) - parseMktCapRaw(a.mkt_cap))
    case 'cap_asc':  return s.sort((a, b) => parseMktCapRaw(a.mkt_cap) - parseMktCapRaw(b.mkt_cap))
    default:         return s
  }
}

export default function Predict() {
  const { user } = useAuth()
  const { submitPrediction, deletePrediction, getPredictionForStock } = usePredictions()
  const [calendar, setCalendar] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedDay, setSelectedDay] = useState(null)
  const [sortKey, setSortKey] = useState('default')
  const predMode = 'basic'
  // Auth nudge: use a dismissible banner instead of a modal overlay
  const [showAuthNudge, setShowAuthNudge] = useState(false)
  const [showAuth, setShowAuth] = useState(false)

  useEffect(() => { document.title = 'Predict Earnings — EarningsEdge' }, [])

  useEffect(() => {
    fetch(`${API_BASE}/api/calendar`)
      .then(r => r.json())
      .then(d => {
        const cal = d.calendar || []
        setCalendar(cal)
        const firstFuture = cal.find(day => isFutureDate(day.date))
        if (firstFuture) setSelectedDay(firstFuture.date)
        else if (cal.length > 0) setSelectedDay(cal[0].date)
      })
      .finally(() => setLoading(false))
  }, [])

  const handlePredict = async (stock, direction, bucket) => {
    const { error, isAnonymous } = await submitPrediction({
      ticker: stock.ticker,
      company_name: stock.name,
      report_date: selectedDay,
      report_time: stock.report_time,
      direction, mode: predMode,
      magnitude_bucket: bucket || null,
    })
    // Show a non-blocking nudge banner instead of a modal overlay
    if (!error && isAnonymous) setShowAuthNudge(true)
  }

  const dayData = calendar.find(d => d.date === selectedDay)
  const canPredict = selectedDay && isFutureDate(selectedDay)

  return (
    <>
    <div className="animate-in">
      <style>{`
        @media (max-width: 600px) {
          .predict-title { font-size: 24px !important; }
          .day-btn { min-width: 90px !important; padding: 8px 10px !important; }
          .sort-pills { gap: 4px !important; }
          .sort-pill { padding: 3px 8px !important; font-size: 10px !important; }
          .stock-row { flex-direction: column !important; align-items: flex-start !important; gap: 8px !important; padding: 12px 12px !important; }
          .stock-meta { width: 100% !important; }
          .stock-actions { width: 100% !important; justify-content: flex-start !important; }
          .predict-btns { width: 100%; }
          .predict-btns button { flex: 1 !important; }
        }
      `}</style>

      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 className="predict-title" style={{ fontFamily: 'var(--display)', fontSize: 32, fontWeight: 700, letterSpacing: '-0.5px', marginBottom: 4 }}>
            Predict Earnings
          </h1>
          <p style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
            Forecast post-earnings moves · Track your accuracy over time
          </p>
        </div>
      </div>

      {/* Auth nudge — inline banner, no overlay, no modal */}
      {showAuthNudge && !user && (
        <div style={{
          background: 'var(--olive-bg2)', border: '1px solid var(--olive-dim)',
          borderRadius: 6, padding: '12px 16px', marginBottom: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
        }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--olive)', marginBottom: 2 }}>Save your predictions</div>
            <div style={{ fontSize: 12, color: 'var(--text2)' }}>Create a free account to track your accuracy over time.</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button onClick={() => setShowAuthNudge(false)} style={{
              padding: '6px 12px', borderRadius: 4, background: 'none',
              border: '1px solid var(--border)', color: 'var(--text2)', fontSize: 12, cursor: 'pointer',
            }}>Later</button>
            <button onClick={() => { setShowAuthNudge(false); setShowAuth(true) }} style={{
              padding: '6px 14px', borderRadius: 4, background: 'var(--olive)',
              border: 'none', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}>Sign up free</button>
          </div>
        </div>
      )}

      {/* Day selector */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, overflowX: 'auto', paddingBottom: 4 }}>
        {loading ? [...Array(5)].map((_, i) => (
          <div key={i} className="day-btn" style={{ minWidth: 110, height: 56, borderRadius: 4, background: 'var(--surface2)' }} />
        )) : calendar.map(day => {
          const isPast = !isFutureDate(day.date)
          const isSelected = selectedDay === day.date
          return (
            <button key={day.date} onClick={() => setSelectedDay(day.date)} className="day-btn" style={{
              minWidth: 110, padding: '10px 14px', borderRadius: 4, flexShrink: 0,
              border: `1px solid ${isSelected ? 'var(--olive)' : 'var(--border)'}`,
              background: isSelected ? 'var(--olive-bg2)' : 'var(--surface)',
              color: isSelected ? 'var(--olive)' : isPast ? 'var(--text3)' : 'var(--text2)',
              cursor: 'pointer', fontFamily: 'var(--sans)', textAlign: 'left',
              opacity: isPast ? 0.55 : 1, transition: 'all 0.15s',
            }}>
              <div style={{ fontSize: 12, fontWeight: 600 }}>{day.day_label}</div>
              <div style={{ fontSize: 11, color: isSelected ? 'var(--olive-light)' : 'var(--text3)', marginTop: 2, fontFamily: 'var(--mono)' }}>
                {isPast ? 'closed' : `${day.stocks?.length || 0} co's`}
                {day.is_today ? ' · Today' : ''}
              </div>
            </button>
          )
        })}
      </div>

      {/* Closed banner */}
      {!canPredict && selectedDay && (
        <div style={{
          background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6,
          padding: '12px 20px', marginBottom: 16, fontSize: 13, color: 'var(--text3)',
          fontFamily: 'var(--mono)', textAlign: 'center',
        }}>
          Predictions closed for this date
        </div>
      )}

      {/* Stock rows */}
      {dayData && (
        <>
          {/* Sort controls */}
          {dayData.stocks?.length > 0 && (
            <div className="sort-pills" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', marginRight: 2 }}>Sort:</span>
              {SORT_OPTIONS.map(opt => (
                <button key={opt.key} onClick={() => setSortKey(opt.key)} className="sort-pill" style={{
                  padding: '4px 10px', borderRadius: 3, cursor: 'pointer',
                  fontFamily: 'var(--mono)', fontSize: 11, fontWeight: sortKey === opt.key ? 600 : 400,
                  border: `1px solid ${sortKey === opt.key ? 'var(--olive)' : 'var(--border)'}`,
                  background: sortKey === opt.key ? 'var(--olive-bg2)' : 'var(--surface)',
                  color: sortKey === opt.key ? 'var(--olive)' : 'var(--text3)',
                  transition: 'all 0.12s',
                }}>{opt.label}</button>
              ))}
            </div>
          )}

          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
            {dayData.stocks?.length === 0 ? (
              <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text3)', fontSize: 13, fontFamily: 'var(--mono)' }}>
                — No earnings scheduled —
              </div>
            ) : sortStocks(dayData.stocks, sortKey).map((stock, i) => {
              const existing = getPredictionForStock(stock.ticker, selectedDay)
              return (
                <div key={stock.ticker} className="stock-row" style={{
                  padding: '12px 20px',
                  borderBottom: i < dayData.stocks.length - 1 ? '1px solid var(--border)' : 'none',
                  background: i % 2 === 0 ? 'var(--surface)' : 'var(--surface2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                }}>
                  {/* Stock info */}
                  <div className="stock-meta" style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--mono)', fontWeight: existing ? 700 : 500, fontSize: 13, color: 'var(--olive)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                      {existing && <span style={{ fontSize: 11, color: 'var(--olive)' }}>✓</span>}
                      {stock.ticker}
                    </span>
                    <span style={{
                      fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px',
                      fontFamily: 'var(--mono)', flexShrink: 0,
                      color: stock.report_time === 'pre' ? '#8b6914' : '#4a6741',
                      background: stock.report_time === 'pre' ? 'rgba(139,105,20,0.08)' : 'rgba(74,103,65,0.08)',
                      padding: '2px 7px', borderRadius: 2,
                    }}>
                      {stock.report_time === 'pre' ? 'AM' : 'PM'}
                    </span>
                    <span style={{ fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 60 }}>
                      {stock.name}
                    </span>
                    {stock.mkt_cap && (
                      <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', flexShrink: 0 }}>
                        {stock.mkt_cap}
                      </span>
                    )}
                  </div>

                  {/* Prediction area */}
                  <div className="stock-actions" style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    {existing ? (
                      <>
                        <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>Your pick:</span>
                        <span style={{
                          fontWeight: 600, fontSize: 12, fontFamily: 'var(--mono)',
                          color: existing.direction === 'up' ? 'var(--green)' : 'var(--red)',
                          background: existing.direction === 'up' ? 'var(--green-bg)' : 'var(--red-bg)',
                          padding: '3px 10px', borderRadius: 3,
                        }}>
                          {existing.magnitude_bucket || (existing.direction === 'up' ? '↑ UP' : '↓ DOWN')}
                        </span>
                        {existing.is_correct != null && (
                          <span style={{ fontSize: 14 }}>{existing.direction_correct ? '✓' : '✗'}</span>
                        )}
                        {canPredict && existing.is_correct == null && (
                          <button onClick={() => deletePrediction(stock.ticker, selectedDay)} style={{
                            background: 'none', border: '1px solid var(--border2)', borderRadius: 3,
                            color: 'var(--text3)', fontSize: 11, cursor: 'pointer',
                            padding: '2px 8px', fontFamily: 'var(--mono)',
                          }}>undo</button>
                        )}
                      </>
                    ) : canPredict ? (
                      <div className="predict-btns" style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => handlePredict(stock, 'up')} style={basicBtn('up')}>↑ Up</button>
                        <button onClick={() => handlePredict(stock, 'down')} style={basicBtn('down')}>↓ Down</button>
                      </div>
                    ) : (
                      <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', flexShrink: 0 }}>closed</span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
    {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
    </>
  )
}

const basicBtn = (dir) => ({
  padding: '7px 18px', borderRadius: 4,
  border: `1px solid ${dir === 'up' ? 'rgba(74,124,63,0.3)' : 'rgba(192,57,43,0.3)'}`,
  background: dir === 'up' ? 'var(--green-bg)' : 'var(--red-bg)',
  color: dir === 'up' ? 'var(--green)' : 'var(--red)',
  fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'var(--sans)',
})
